import React from 'react';
import {
  UserCheckIcons,
  UsersIcons,
} from '../../../../../../../../../icons/icons';
import { NameEmailAvatar } from '../../../../../../../../../ui';
import { QuestionAndAnswer } from '../../Components/QuestionAndAnswer';
import { QuestionCollapsible } from '../../Components/QuestionCollapsible';
import { StarScore } from '../../Components/StartScore';
import { DetailedResultsSkeleton } from './Components/DetailedResultsSkeleton';
import { useDetailedResults } from './Hooks/UseDetailedResults';
import { IDetailedResults } from './Interfaces/Interfaces';

// Add custom CSS for animations
const styles = `
  @keyframes slideIn {
    from {
      width: 0%;
    }
    to {
      width: var(--target-width);
    }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translate3d(0, 20px, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }
  
  .fade-in-up {
    animation: fadeInUp 0.6s ease-out;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export const DetailedResults: React.FC<IDetailedResults> = ({
  beiResults,
  assessmentData: initialAssessmentData,
  loading,
  error,
}) => {
  const {
    assessmentSections,
    candidateInfo,
    summaryStats,
    hasAssessmentData,
    hasBeiResults,
    openSections,
    handleSectionToggle,
    detailedQuestions,
    formatTime,
    updateDetailedQuestionArray,
    behavioralSections,
    handleToggleQuestion,
    openQuestions,
    handleQuestionStatusChange,
  } = useDetailedResults({
    beiResults,
    assessmentData: initialAssessmentData,
    loading,
    error,
  });
  // Handle loading state
  if (loading) {
    return <DetailedResultsSkeleton />;
  }

  // Handle error state
  if (error) {
    return (
      <div className='w-full h-[calc(100vh-8rem)] overflow-y-auto'>
        <div className='px-6 py-6'>
          <div className='bg-red-50 border border-red-200 rounded-lg p-6 text-center'>
            <div className='text-red-500 text-lg mb-2'>
              Error Loading Results
            </div>
            <div className='text-red-700 mb-4'>{ error }</div>
            <button
                onClick={() => window.location.reload()}
                className='px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors'
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasBeiResults && !hasAssessmentData) {
    return (
      <div className='w-full h-[calc(100vh-8rem)] overflow-y-auto'>
        <div className='px-6 py-6'>
          <div className='bg-gray-50 border border-gray-200 rounded-lg p-12 text-center'>
            <div className='text-gray-400 text-6xl mb-4'>📋</div>
            <h3 className='text-xl font-medium text-gray-600 mb-2'>
              No Detailed Results Available
            </h3>
            <p className='text-gray-500 mb-4'>
              No assessment data could be loaded at this time.
            </p>
            <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left max-w-md mx-auto'>
              <h4 className='font-medium text-yellow-800 mb-2'>
                Possible reasons:
              </h4>
              <ul className='text-sm text-yellow-700 space-y-1'>
                <li>• Assessment is still in progress</li>
                <li>• Technical assessment data is not available</li>
                <li>• Behavioral interview has not been completed</li>
                <li>• Server error occurred while loading data</li>
              </ul>
            </div>
            <button
                onClick={() => window.location.reload()}
                className='mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors'
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className='w-full h-[calc(100vh-8rem)] overflow-y-auto bg-[#fbfbfd]'>
      <div className='p-3 pb-12 mx-auto'>
        <div className='mb-4 bg-white/80 backdrop-blur-md rounded-none px-5 py-3 shadow-[0_2px_12px_-3px_rgba(0,0,0,0.08)] border-b border-gray-100'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <NameEmailAvatar
                  name={candidateInfo.name}
                  email={candidateInfo.email}
                  avatar={''}
              />
              <div>
                <p className='text-gray-600 text-xs font-medium'>
                  { candidateInfo.company }
                </p>
                <p className='text-[10px] text-gray-400'>
                  { candidateInfo.date }
                </p>
              </div>
            </div>
          </div>
        </div>
        { assessmentSections.length > 0 && (
          <div className='mb-8'>
            <div className='mb-3 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-none border-t-2 border-t-blue-500 border-x border-b border-gray-100/50 shadow-sm sticky top-0 z-10'>
              <div className='flex items-center gap-2.5'>
                <div className='p-1 bg-blue-50'>
                  <UserCheckIcons className='w-3.5 h-3.5 text-blue-500' />
                </div>
                <div>
                  <h2 className='text-sm font-bold text-gray-800 uppercase tracking-tight'>
                    Skills Assessment
                  </h2>
                </div>
              </div>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5'>
              { assessmentSections.map((section) => (
                <div
                    key={section.title}
                    className={`${
                      openSections.has(section.title)
                        ? 'md:col-span-2 lg:col-span-3'
                        : ''
                    }`}
                >
                  <div
                      className={`rounded-none border-t-2 transition-all duration-300 ${
                        openSections.has(section.title)
                          ? 'bg-cyan-200/40 border-t-blue-500 shadow-[0_8px_30px_rgb(0,0,0,0.08)] scale-[1.01] z-[5]'
                          : 'bg-blue-100/30 border-t-blue-400/30 border-x border-b border-gray-100 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_20px_-5px_rgba(0,0,0,0.1)] hover:-translate-y-1'
                      }`}
                  >
                    { /* Section Header */ }
                    <div
                        className='p-2.5 cursor-pointer'
                        onClick={() => handleSectionToggle(section.title)}
                    >
                      <div className='flex items-start justify-between mb-2'>
                        <div className='flex-1'>
                          <h3 className='text-sm font-semibold text-gray-900 mb-1 line-clamp-2'>
                            { section.title }
                          </h3>
                          <div className='flex items-center gap-2 text-xs text-gray-500'>
                            <span>{ section.totalQuestions } Questions</span>
                            { section.followUpQuestions > 0 && (
                              <>
                                <span>•</span>
                                <span className='text-orange-600'>
                                  { section.followUpQuestions } Follow-ups
                                </span>
                              </>
                            ) }
                          </div>
                        </div>
                        <div className='flex items-center gap-3'>
                          <div className='text-right'>
                            <div
                                className={`text-xl font-bold tracking-tight ${
                                  section.score >= 75
                                    ? 'text-blue-600'
                                    : section.score >= 50
                                      ? 'text-blue-500'
                                      : section.score >= 25
                                        ? 'text-orange-500'
                                        : 'text-red-500'
                                }`}
                            >
                              { section.score }%
                            </div>
                          </div>
                          <svg
                              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                                openSections.has(section.title)
                                  ? 'rotate-180'
                                  : ''
                              }`}
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'
                          >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M19 9l-7 7-7-7'
                            />
                          </svg>
                        </div>
                      </div>

                      { /* Assessment Statistics */ }
                      <div className='mt-2 pt-2 border-t border-gray-100/60'>
                        <div className='grid grid-cols-2 gap-2 text-xs'>
                          <div className='text-gray-600 font-medium'>
                            <span className='text-gray-400'>Correct:</span>{ ' ' }
                            <span className='text-gray-900'>
                              {
                                detailedQuestions.filter(
                                  (q) =>
                                    q.skillId === section.skillId &&
                                    q.isCorrect,
                                ).length
                              }
                            </span>
                          </div>
                          <div className='text-gray-600 font-medium'>
                            <span className='text-gray-400'>Accuracy:</span>{ ' ' }
                            <span className='text-blue-600'>
                              { Math.round(
                                (detailedQuestions.filter(
                                  (q) =>
                                    q.skillId === section.skillId &&
                                    q.isCorrect,
                                ).length /
                                  detailedQuestions.filter(
                                    (q) => q.skillId === section.skillId,
                                  ).length) *
                                  100 || 0,
                              ) }
                              %
                            </span>
                          </div>
                          <div className='text-gray-600 font-medium'>
                            <span className='text-gray-400'>Total:</span>{ ' ' }
                            <span className='text-gray-900'>
                              {
                                detailedQuestions.filter(
                                  (q) => q.skillId === section.skillId,
                                ).length
                              }
                            </span>
                          </div>
                          <div className='text-gray-600 font-medium'>
                            <span className='text-gray-400'>Avg Time:</span>{ ' ' }
                            <span className='font-mono text-gray-900'>
                              { (() => {
                                const questionsForSkill =
                                  detailedQuestions.filter(
                                    (q) => q.skillId === section.skillId,
                                  );
                                const totalSeconds = questionsForSkill.reduce(
                                  (sum, q) => sum + q.timeTaken,
                                  0,
                                );
                                const avgSeconds =
                                  questionsForSkill.length > 0
                                    ? Math.round(
                                      totalSeconds / questionsForSkill.length,
                                    )
                                    : 0;
                                return formatTime(avgSeconds);
                              })() }
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    { /* Expanded Content Section (Same Container) */ }
                    { openSections.has(section.title) && (
                      <div className='p-5 border-t border-gray-100 bg-white'>
                        <div className='flex items-center justify-between mb-4 pb-2 border-b border-gray-50'>
                          <h4 className='text-[11px] font-bold text-gray-400 uppercase tracking-widest'>
                            Questions & Answers
                          </h4>
                          <span className='text-[11px] font-medium text-gray-400'>
                            {
                              detailedQuestions.filter(
                                (q) => q.skillId === section.skillId,
                              ).length
                            }{ ' ' }
                            question(s)
                          </span>
                        </div>
                        <div className='space-y-3'>
                          { detailedQuestions
                            .filter((q) => q.skillId === section.skillId)
                            .map((question) => (
                              <QuestionCollapsible
                                  key={question.id}
                                  assessmentQuestionResult={question}
                                  updateDetailedQuestionArray={
                                    updateDetailedQuestionArray
                                  }
                                  showSystemAnswer
                              />
                            )) }
                        </div>
                      </div>
                    ) }
                  </div>
                </div>
              )) }
            </div>
          </div>
        ) }
        { /* Behavioral Interview Section */ }
        { behavioralSections.length > 0 && (
          <div className='mb-8'>
            <div className='mb-3 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-none border-t-2 border-t-green-500 border-x border-b border-gray-100/50 shadow-sm sticky top-0 z-10'>
              <div className='flex items-center gap-2.5'>
                <div className='p-1 bg-green-50'>
                  <UsersIcons className='w-3.5 h-3.5 text-green-500' />
                </div>
                <div>
                  <h2 className='text-sm font-bold text-gray-800 uppercase tracking-tight'>
                    Behavioral Interview (BEI)
                  </h2>
                </div>
              </div>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5'>
              { behavioralSections.map((section) => (
                <div
                    key={section.title}
                    className={`${
                      openSections.has(section.title)
                        ? 'md:col-span-2 lg:col-span-3'
                        : ''
                    }`}
                >
                  <div
                      className={`rounded-none border-t-2 transition-all duration-300 ${
                        openSections.has(section.title)
                          ? 'bg-emerald-200/40 border-t-green-500 shadow-[0_8px_30px_rgb(0,0,0,0.08)] scale-[1.01] z-[5]'
                          : 'bg-emerald-100/30 border-t-green-400/30 border-x border-b border-gray-100 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_20px_-5px_rgba(0,0,0,0.1)] hover:-translate-y-1'
                      }`}
                  >
                    { /* Section Header */ }
                    <div
                        className='p-2.5 cursor-pointer'
                        onClick={() => handleSectionToggle(section.title)}
                    >
                      <div className='flex items-start justify-between mb-2'>
                        <div className='flex-1'>
                          <h3 className='text-sm font-semibold text-gray-900 mb-1 line-clamp-2'>
                            { section.title }
                          </h3>
                          <div className='flex items-center gap-2 text-xs text-gray-500'>
                            <span>{ section.totalQuestions } Questions</span>
                            { section.followUpQuestions > 0 && (
                              <>
                                <span>•</span>
                                <span className='text-orange-600'>
                                  { section.followUpQuestions } Follow-ups
                                </span>
                              </>
                            ) }
                          </div>
                        </div>
                        <div className='flex items-center gap-3'>
                          <div className='text-right'>
                            <div
                                className={`text-xl font-bold tracking-tight ${
                                  section.score >= 75
                                    ? 'text-blue-600'
                                    : section.score >= 50
                                      ? 'text-blue-500'
                                      : section.score >= 25
                                        ? 'text-orange-500'
                                        : 'text-red-500'
                                }`}
                            >
                              { section.score }%
                            </div>
                          </div>
                          <svg
                              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                                openSections.has(section.title)
                                  ? 'rotate-180'
                                  : ''
                              }`}
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'
                          >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M19 9l-7 7-7-7'
                            />
                          </svg>
                        </div>
                      </div>

                      { /* STAR Breakdown */ }
                      { section.starBreakdown && (
                        <div className='mt-2 pt-2 border-t border-gray-100/60'>
                          <StarScore breakdown={section.starBreakdown} />
                        </div>
                      ) }
                    </div>

                    { /* Expanded Content Section (Same Container) */ }
                    { openSections.has(section.title) && (
                      <div className='p-5 border-t border-gray-100 bg-white'>
                        <div className='flex items-center justify-between mb-4 pb-2 border-b border-gray-50'>
                          <h4 className='text-[11px] font-bold text-gray-400 uppercase tracking-widest'>
                            Questions & Responses
                          </h4>
                          <div className='text-[11px] font-medium text-gray-400'>
                            { openQuestions.size > 0
                              ? `${openQuestions.size} expanded`
                              : `${section.questions.length} question(s)` }
                          </div>
                        </div>
                        <div className='space-y-3'>
                          { section.questions.map((q, idx) => {
                            const questionId = `${section.title}-${idx}`;
                            return (
                              <div
                                  key={`${section.title}-${idx}-${q.videoResponse}`}
                                  className='bg-white rounded-none border border-gray-100 overflow-hidden hover:shadow-md hover:border-gray-200 transition-all duration-300'
                              >
                                <QuestionAndAnswer
                                    starBreakdown={q.starBreakdown}
                                    followUpQuestions={q.followUpQuestions}
                                    handleOpenQuestion={() =>
                                      handleToggleQuestion(questionId)
                                    }
                                    openQuestion={
                                      openQuestions.has(questionId)
                                        ? q.question
                                        : null
                                    }
                                    question={q.question}
                                    response={q.response}
                                    score={q.score}
                                    skillAttribute={q.skillAttribute}
                                    timeTaken={q.timeTaken}
                                    videoResponse={q.videoResponse}
                                />
                              </div>
                            );
                          }) }
                        </div>
                      </div>
                    ) }
                  </div>
                </div>
              )) }
            </div>
          </div>
        ) }
      </div>
    </div>
  );
};
